/**
 * Client for the DMI Analytics API.
 *
 * The API is reached same-origin through the nginx proxy, which the content
 * security policy requires. Session and CSRF state live in cookies set by the
 * API, so every request must send credentials.
 */
import type {
  ChartType,
  Dashboard,
  DashboardSummary,
  Panel,
  PanelEncoding,
  PanelLayout,
  PanelQuery,
  PanelResult,
  UserSession,
  Artifact,
  ArtifactSection,
  ArtifactSummary,
  ArtifactVersionSummary,
} from "../types";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
const CSRF_COOKIE = "dmi_csrf";

/** The API's error envelope, as declared in the shared contracts. */
export interface SafeErrorBody {
  code: string;
  message: string;
  requestId?: string;
  traceId?: string;
  retryable?: boolean;
  fieldErrors?: { field: string; message: string }[];
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId?: string;
  readonly retryable: boolean;
  readonly fieldErrors: { field: string; message: string }[];

  constructor(status: number, body: SafeErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.requestId = body.requestId;
    this.retryable = body.retryable ?? false;
    this.fieldErrors = body.fieldErrors ?? [];
  }
}

export function readCsrfToken(): string {
  const match = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${CSRF_COOKIE}=`));
  return match ? decodeURIComponent(match.slice(CSRF_COOKIE.length + 1)) : "";
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body !== undefined) {
    headers.set("content-type", "application/json");
  }
  if (method !== "GET" && method !== "HEAD") {
    headers.set("x-csrf-token", readCsrfToken());
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      method,
      headers,
      credentials: "include",
    });
  } catch {
    // A network failure carries no server correlation id, so name it clearly
    // rather than presenting it as an API rejection.
    throw new ApiError(0, {
      code: "network_unavailable",
      message: "The analytics service could not be reached.",
      retryable: true,
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const isJson = response.headers
    .get("content-type")
    ?.includes("json");

  if (!response.ok) {
    // An HTML body here means the request never reached the API, which is what
    // a missing proxy route looks like.
    const body: SafeErrorBody = isJson
      ? await response.json()
      : {
          code: "unexpected_response",
          message: "The analytics service returned an unexpected response.",
        };
    throw new ApiError(response.status, body);
  }

  if (!isJson) {
    throw new ApiError(response.status, {
      code: "unexpected_response",
      message: "The analytics service returned an unexpected response.",
    });
  }

  return (await response.json()) as T;
}

interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
}

function toSession(user: SessionUser, ttlSeconds = 8 * 60 * 60): UserSession {
  return {
    id: user.id,
    displayName: user.displayName,
    email: user.email,
    roles: user.roles,
    expiresAt: new Date(Date.now() + ttlSeconds * 1000).toISOString(),
  };
}

/** A published context and the engine that serves it. */
export interface GovernedSource {
  id: string;
  name: string;
  description: string;
  engine: string;
  contextVersion: string;
  status: string;
  engineHealth: string;
  entityCount: number;
  metricCount: number;
  updatedAt: string;
  /** The system the certified data physically comes from: snowflake, duckdb. */
  sourceSystem: string;
  catalogs: string[];
  schemas: string[];
  /** False when the context has no runtime binding, so it cannot be queried. */
  queryable: boolean;
  approvedBy: string;
  createdAt: string;
  relationshipCount: number;
  ruleCount: number;
  availableVersions: string[];
  /**
   * The business audience the source serves. Null or absent for a context
   * nobody has classified, which the landing page groups under Unassigned
   * rather than filing under a guess.
   */
  dataDomain?: string | null;
  /** Email of whoever created the studio project. Empty for an unowned context. */
  createdBy?: string;
  /** The studio project this model was published from. */
  sourceProjectId?: string;
}

export interface AccessGroup {
  id: string;
  name: string;
  memberIds: string[];
}

export interface AccessGrant {
  id: string;
  granteeType: string;
  granteeId: string;
  resourceType: string;
  resourceId: string;
  role: string;
}

export const accessApi = {
  groups: () => request<AccessGroup[]>("/access/groups"),
  users: () => request<Array<{ id: string; email: string; displayName: string }>>(
    "/access/users"),
  createGroup: (name: string) => request<AccessGroup>("/access/groups", {
    method: "POST", body: JSON.stringify({ name }),
  }),
  deleteGroup: (id: string) => request<void>(`/access/groups/${id}`, { method: "DELETE" }),
  addMember: (groupId: string, userId: string) => request<AccessGroup>(
    `/access/groups/${groupId}/members`,
    { method: "POST", body: JSON.stringify({ userId }) },
  ),
  removeMember: (groupId: string, userId: string) => request<AccessGroup>(
    `/access/groups/${groupId}/members/${userId}`, { method: "DELETE" },
  ),
  grants: (resourceType: string, resourceId: string) => request<AccessGrant[]>(
    `/access/grants?resourceType=${encodeURIComponent(resourceType)}&resourceId=${encodeURIComponent(resourceId)}`,
  ),
  setGrant: (grant: Omit<AccessGrant, "id">) => request<AccessGrant>("/access/grants", {
    method: "PUT", body: JSON.stringify(grant),
  }),
  removeGrant: (id: string) => request<void>(`/access/grants/${id}`, { method: "DELETE" }),
  shareWithProject: (body: {
    projectId: string; resourceType: string; resourceId: string; role: string;
  }) => request<AccessGrant[]>("/access/share-with-project", {
    method: "POST", body: JSON.stringify(body),
  }),
  audit: () => request<Array<{ id: string; action: string; detail: Record<string, string>; createdAt: string }>>(
    "/access/audit"),
};

export const sourcesApi = {
  async list(): Promise<GovernedSource[]> {
    return request<GovernedSource[]>("/data-sources");
  },
};

export interface WhatIfMetric {
  member: string;
  label: string;
  aggregation: "sum" | "avg" | "count" | "count_distinct";
  description: string;
  unit?: string | null;
}

export interface WhatIfDimension {
  member: string;
  label: string;
  temporal: boolean;
}

export type WhatIfPeriod =
  | "last_month"
  | "last_quarter"
  | "year_to_date"
  | "last_12_months"
  | "all_available";

export interface WhatIfOptions {
  contextId: string;
  contextVersion: string;
  metrics: WhatIfMetric[];
  dimensions: WhatIfDimension[];
  periods: WhatIfPeriod[];
}

export interface WhatIfRequest {
  contextId: string;
  contextVersion?: string | null;
  metric: string;
  dimension?: string | null;
  period: WhatIfPeriod;
  relativePercent: number;
  limit?: number;
}

export interface WhatIfResult {
  executionKind: "simulation";
  metric: string;
  metricLabel: string;
  dimension?: string | null;
  period: WhatIfPeriod;
  relativePercent: number;
  baselineTotal?: string | null;
  scenarioTotal?: string | null;
  deltaTotal?: string | null;
  rows: {
    label: string;
    baseline?: string | null;
    scenario?: string | null;
    delta?: string | null;
  }[];
  assumptions: string[];
  disclaimer: string;
  warnings: string[];
  provenance: {
    contextId: string;
    contextVersion: string;
    baselineQueryIds: string[];
    baselineExecutedAt: string;
    sourceSql: string[];
    method: "relative_percent_v1";
    specHash: string;
  };
}

export const whatIfApi = {
  async options(
    contextId: string,
    contextVersion?: string | null,
  ): Promise<WhatIfOptions> {
    const params = new URLSearchParams({ context_id: contextId });
    if (contextVersion) params.set("context_version", contextVersion);
    return request<WhatIfOptions>(`/what-if/options?${params}`);
  },

  async evaluate(payload: WhatIfRequest): Promise<WhatIfResult> {
    return request<WhatIfResult>("/what-if/evaluate", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};

export type ScenarioLeverUnit =
  | "percent"
  | "points"
  | "currency"
  | "currency_per_hour"
  | "count"
  | "hours"
  | "months"
  | "seconds";

export type ScenarioValueUnit =
  | "currency"
  | "currency_per_hour"
  | "hours"
  | "percent"
  | "count"
  | "months"
  | "seconds";

export type ScenarioIcon =
  | "gauge"
  | "tag"
  | "users"
  | "user-plus"
  | "calculator"
  | "headset"
  | "phone-missed"
  | "shuffle"
  | "messages";

export interface ScenarioLever {
  id: string;
  label: string;
  unit: ScenarioLeverUnit;
  minimum: number;
  maximum: number;
  step: number;
  default: number;
  help: string;
  /** Drivers are what is being tested; assumptions price them. */
  group: "driver" | "assumption";
}

export interface ScenarioPreset {
  id: string;
  label: string;
  description: string;
  levers: Record<string, number>;
}

export interface ScenarioTemplate {
  id: string;
  title: string;
  question: string;
  summary: string;
  icon: ScenarioIcon;
  levers: ScenarioLever[];
  presets: ScenarioPreset[];
  breakdowns: { member: string; label: string }[];
  /** The scenario is computed per group, so one grouping is always applied. */
  breakdownRequired?: boolean;
  periods: WhatIfPeriod[];
  defaultPeriod: WhatIfPeriod;
  available: boolean;
  unavailableReason?: string | null;
  /** Built by the signed-in user rather than shipped with the product. */
  custom?: boolean;
}

export interface ScenarioCatalogue {
  contextId: string;
  contextVersion: string;
  dataDomain?: string | null;
  studioTitle: string;
  studioSummary: string;
  templates: ScenarioTemplate[];
}

export interface ScenarioEvaluateRequest {
  contextId: string;
  contextVersion?: string | null;
  templateId: string;
  period?: WhatIfPeriod | null;
  breakdown?: string | null;
  levers: Record<string, number>;
}

export interface ScenarioKpi {
  id: string;
  label: string;
  unit: ScenarioValueUnit;
  baseline?: number | null;
  scenario?: number | null;
  delta?: number | null;
  higherIsBetter?: boolean | null;
  emphasis: boolean;
}

export interface ScenarioBridgeStep {
  label: string;
  value: number;
  kind: "total" | "increase" | "decrease";
}

export interface ScenarioTable {
  title: string;
  dimensionLabel: string;
  columns: { key: string; label: string; unit: ScenarioValueUnit }[];
  rows: Record<string, number | string | null>[];
  chartKeys: string[];
  truncated: boolean;
}

export interface ScenarioSensitivity {
  title: string;
  rowLabel: string;
  rowUnit: ScenarioLeverUnit;
  columnLabel: string;
  columnUnit: ScenarioLeverUnit;
  valueUnit: ScenarioValueUnit;
  rows: number[];
  columns: number[];
  values: (number | null)[][];
  currentRow: number;
  currentColumn: number;
}

export interface ScenarioTrend {
  title: string;
  unit: ScenarioValueUnit;
  points: { label: string; baseline?: number | null; scenario?: number | null }[];
}

export interface ScenarioResult {
  executionKind: "simulation";
  templateId: string;
  title: string;
  period: WhatIfPeriod;
  breakdown?: string | null;
  levers: Record<string, number>;
  headline: string;
  kpis: ScenarioKpi[];
  bridge: ScenarioBridgeStep[];
  bridgeTitle?: string | null;
  bridgeUnit?: ScenarioValueUnit;
  table?: ScenarioTable | null;
  sensitivity?: ScenarioSensitivity | null;
  trend?: ScenarioTrend | null;
  insights: string[];
  assumptions: string[];
  disclaimer: string;
  warnings: string[];
  provenance: {
    contextId: string;
    contextVersion: string;
    baselineQueryIds: string[];
    baselineExecutedAt: string;
    method: string;
    specHash: string;
    cached: boolean;
  };
}

export const scenarioApi = {
  async catalogue(
    contextId: string,
    contextVersion?: string | null,
  ): Promise<ScenarioCatalogue> {
    const params = new URLSearchParams({ context_id: contextId });
    if (contextVersion) params.set("context_version", contextVersion);
    return request<ScenarioCatalogue>(`/what-if/scenarios?${params}`);
  },

  async evaluate(payload: ScenarioEvaluateRequest): Promise<ScenarioResult> {
    return request<ScenarioResult>("/what-if/scenarios/evaluate", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};

export interface CustomScenarioMetric {
  /** The name formulas read the metric by. */
  name: string;
  member: string;
  label: string;
}

export interface CustomScenarioLever {
  id: string;
  label: string;
  unit: ScenarioLeverUnit;
  minimum: number;
  maximum: number;
  step: number;
  default: number;
  /** The value that means "no change"; today is calculated with it. */
  neutral: number;
  help: string;
  group: "driver" | "assumption";
}

export interface CustomScenarioOutput {
  id: string;
  label: string;
  unit: ScenarioValueUnit;
  formula: string;
  higherIsBetter?: boolean | null;
  emphasis: boolean;
}

export interface CustomScenarioDefinition {
  contextId: string;
  title: string;
  question: string;
  summary: string;
  icon: ScenarioIcon;
  metrics: CustomScenarioMetric[];
  levers: CustomScenarioLever[];
  outputs: CustomScenarioOutput[];
  breakdowns: string[];
  defaultPeriod: WhatIfPeriod;
}

export interface CustomScenario extends CustomScenarioDefinition {
  id: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

const CUSTOM_SCENARIOS = "/what-if/custom-scenarios";

/** Scenarios each user builds for themselves; nobody else sees them. */
export const customScenarioApi = {
  async list(contextId: string): Promise<CustomScenario[]> {
    const params = new URLSearchParams({ context_id: contextId });
    return request<CustomScenario[]>(`${CUSTOM_SCENARIOS}?${params}`);
  },

  async get(id: string): Promise<CustomScenario> {
    return request<CustomScenario>(`${CUSTOM_SCENARIOS}/${encodeURIComponent(id)}`);
  },

  async create(definition: CustomScenarioDefinition): Promise<CustomScenario> {
    return request<CustomScenario>(CUSTOM_SCENARIOS, {
      method: "POST",
      body: JSON.stringify(definition),
    });
  },

  async update(
    id: string,
    definition: CustomScenarioDefinition,
    revision: number,
  ): Promise<CustomScenario> {
    return request<CustomScenario>(`${CUSTOM_SCENARIOS}/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify({ ...definition, revision }),
    });
  },

  async remove(id: string): Promise<void> {
    return request<void>(`${CUSTOM_SCENARIOS}/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
};

export interface ThreadSummary {
  threadId: string;
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
  /** The first thing the user asked, so the thread is recognisable. */
  preview: string;
  userId?: string;
  /**
   * Whose conversation it is, sent only to an administrator.
   *
   * Absent for everybody else because every thread they can see is their own,
   * and present for an administrator precisely because they are not.
   */
  owner?: string;
}

export interface StoredMessage {
  messageId: string;
  threadId: string;
  role: string;
  content: string;
  sequence: number;
  createdAt: string;
}

/** One AG-UI event as the runtime stored it, replayable into a trace step. */
export interface StoredRunEvent {
  eventId: string;
  runId: string;
  sequence: number;
  eventType: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

/** Conversation history, read through the API from the agent runtime's store. */
export const historyApi = {
  async threads(limit = 50): Promise<ThreadSummary[]> {
    const body = await request<{ threads: ThreadSummary[] }>(
      `/threads?limit=${encodeURIComponent(String(limit))}`,
    );
    return body.threads ?? [];
  },

  async messages(threadId: string): Promise<StoredMessage[]> {
    const body = await request<{ messages: StoredMessage[] }>(
      `/threads/${encodeURIComponent(threadId)}/messages`,
    );
    return body.messages ?? [];
  },

  /** The run events behind a conversation, so its trace can be rebuilt. */
  async events(threadId: string, limit = 500): Promise<StoredRunEvent[]> {
    const body = await request<{ events: StoredRunEvent[] }>(
      `/threads/${encodeURIComponent(threadId)}/events` +
        `?limit=${encodeURIComponent(String(limit))}`,
    );
    return body.events ?? [];
  },
};

/** A chart as the client proposes it, before the server owns its identity. */
export interface PanelDraft {
  /** Optional client-chosen id, kept by the server so callers can match results. */
  id?: string;
  title: string;
  chartType: ChartType;
  query: PanelQuery;
  contextId?: string;
  contextVersion?: string | null;
  encoding?: Partial<PanelEncoding>;
  layout?: PanelLayout;
  narrative?: string;
}

/**
 * How the agent answered a single-chart question.
 *
 * `ok` carries one panel, `multiple` several candidates of which one must be
 * chosen, `clarify` a question with `options`, and `unsupported`/`invalid` only
 * a `message`. None of these are failures of the request itself.
 */
export type PanelPlanStatus =
  | "ok"
  | "multiple"
  | "clarify"
  | "unsupported"
  | "invalid";

export interface PanelPlan {
  status: PanelPlanStatus;
  message: string;
  panels: PanelDraft[];
  options: string[];
  contextId: string;
  contextVersion: string;
}

/** Turns one natural-language question into a governed, validated chart. */
export const panelPlansApi = {
  async plan(body: {
    question: string;
    contextId: string;
    contextVersion?: string;
  }): Promise<PanelPlan> {
    return request<PanelPlan>("/panel-plans", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};

/**
 * Sections a report or artifact canvas offers. Headline number, chart and
 * table are governed queries; overview, key insights and summary are written
 * from those queries' rows.
 */
export type DocumentSectionKind =
  | "overview"
  | "headline_number"
  | "chart"
  | "table"
  | "key_insights"
  | "summary";

export interface DocumentSectionDraft {
  kind: DocumentSectionKind;
  instruction: string;
  /** The planned chart; required for data sections, absent for written ones. */
  panel?: PanelDraft;
}

export interface DocumentDraft {
  artifact: Artifact;
  results: PanelResult[];
}

/** Composes and saves a draft document from planned and written sections. */
export const documentDraftsApi = {
  async compose(body: {
    title: string;
    subject?: string;
    contextId: string;
    contextVersion?: string;
    sections: DocumentSectionDraft[];
  }): Promise<DocumentDraft> {
    return request<DocumentDraft>("/document-drafts", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};

export type ExportMode = "executive" | "operational";
export type ExportFormat = "pdf" | "pptx";

/**
 * Dashboards, saved charts, and their governed data.
 *
 * Data and definitions are fetched separately on purpose. `data` returns the
 * last cached execution so a dashboard paints immediately on open; `refresh`
 * re-executes the stored semantic queries, which is the only path that
 * produces new numbers.
 */
/**
 * Governed documents.
 *
 * A revision is a POST that creates a version rather than a PUT that replaces
 * one, which is the whole point: a document that has been sent stays readable
 * exactly as it was sent, so `get` takes an optional version.
 */
export const artifactsApi = {
  async list(hidden = false): Promise<ArtifactSummary[]> {
    return request<ArtifactSummary[]>(`/artifacts?hidden=${hidden}`);
  },

  async get(artifactId: string, version?: number): Promise<Artifact> {
    const suffix = version === undefined ? "" : `?version=${version}`;
    return request<Artifact>(
      `/artifacts/${encodeURIComponent(artifactId)}${suffix}`,
    );
  },

  /**
   * Last cached rows for the document's chart sections, keyed by panel.
   *
   * Fetched separately from the document for the same reason a dashboard's
   * data is: a stored document has no streamed previews, so without this it
   * would open with empty charts until each section was refreshed by hand.
   */
  async data(artifactId: string, version?: number): Promise<PanelResult[]> {
    const suffix = version === undefined ? "" : `?version=${version}`;
    return request<PanelResult[]>(
      `/artifacts/${encodeURIComponent(artifactId)}/data${suffix}`,
    );
  },

  /** The document's history, newest first. Carries no sections. */
  async versions(artifactId: string): Promise<ArtifactVersionSummary[]> {
    return request<ArtifactVersionSummary[]>(
      `/artifacts/${encodeURIComponent(artifactId)}/versions`,
    );
  },

  async revise(
    artifactId: string,
    body: {
      sections: ArtifactSection[];
      title?: string;
      revisionNote?: string;
    },
  ): Promise<Artifact> {
    return request<Artifact>(
      `/artifacts/${encodeURIComponent(artifactId)}/revisions`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  /** Marks a version as issued, which is what makes it citable. */
  async publish(artifactId: string, version?: number): Promise<Artifact> {
    return request<Artifact>(
      `/artifacts/${encodeURIComponent(artifactId)}/publish`,
      { method: "POST", body: JSON.stringify({ version }) },
    );
  },

  async hide(artifactId: string): Promise<void> {
    await request<void>(
      `/artifacts/${encodeURIComponent(artifactId)}/hide`,
      { method: "POST" },
    );
  },

  async restore(artifactId: string): Promise<void> {
    await request<void>(
      `/artifacts/${encodeURIComponent(artifactId)}/restore`,
      { method: "POST" },
    );
  },

  /**
   * Downloads the document as a PDF or a deck.
   *
   * Not routed through `request`, which assumes a JSON body: the response is a
   * binary document. `version` is accepted because an issued document has to
   * stay exportable exactly as it was sent.
   */
  async export(
    artifactId: string,
    format: ExportFormat,
    mode: ExportMode,
    images: { panelId: string; dataUrl: string }[],
    version?: number,
  ): Promise<Blob> {
    const suffix = version === undefined ? "" : `?version=${version}`;
    const response = await fetch(
      `${BASE_URL}/artifacts/${encodeURIComponent(artifactId)}` +
        `/export/${format}${suffix}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": readCsrfToken(),
        },
        body: JSON.stringify({ mode, images }),
      },
    );
    if (!response.ok) {
      const body: SafeErrorBody = await response.json().catch(() => ({
        code: "export_failed",
        message: "The export could not be produced.",
      }));
      throw new ApiError(response.status, body);
    }
    return response.blob();
  },
};

export const goldQueriesApi = {
  /**
   * Keeps a question and its governed answer on the context pack.
   *
   * No figure is sent. The context layer re-runs the query and records what it
   * returns, so a certified number cannot come from whatever the browser was
   * showing. The approver is taken from the session server-side for the same
   * reason, which is why it is absent here.
   */
  async accept(body: {
    contextId: string;
    contextVersion: string;
    question: string;
    semanticQuery: { metrics: string[]; dimensions: string[]; limit?: number };
  }): Promise<{ goldQueryCount?: number; gold_query_count?: number }> {
    return request("/gold-queries", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};

export const dashboardsApi = {
  /** Visible dashboards, or the hidden ones when asked. */
  async list(hidden = false): Promise<DashboardSummary[]> {
    return request<DashboardSummary[]>(`/dashboards?hidden=${hidden}`);
  },

  async get(dashboardId: string): Promise<Dashboard> {
    return request<Dashboard>(`/dashboards/${encodeURIComponent(dashboardId)}`);
  },

  async create(body: {
    name: string;
    description?: string;
    contextId?: string;
    contextVersion?: string | null;
    panels?: PanelDraft[];
    panelIds?: string[];
  }): Promise<Dashboard> {
    return request<Dashboard>("/dashboards", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async update(
    dashboardId: string,
    body: {
      name?: string;
      description?: string;
      status?: string;
      hidden?: boolean;
    },
  ): Promise<Dashboard> {
    return request<Dashboard>(
      `/dashboards/${encodeURIComponent(dashboardId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    );
  },

  /**
   * Takes a dashboard out of the workspace without destroying it.
   *
   * The workspace offers this rather than delete, because deleting cascades
   * into the dashboard's panels: a chart combined into a dashboard would be
   * gone for good.
   */
  async setHidden(dashboardId: string, hidden: boolean): Promise<Dashboard> {
    return this.update(dashboardId, { hidden });
  },

  /** Cached results. Empty simply means the dashboard has not been run yet. */
  async data(dashboardId: string): Promise<PanelResult[]> {
    const body = await request<{ results: PanelResult[] }>(
      `/dashboards/${encodeURIComponent(dashboardId)}/data`,
    );
    return body.results ?? [];
  },

  /**
   * Re-executes stored semantic queries. Naming panels retries just those,
   * which is what makes a single failed tile recoverable.
   */
  async refresh(
    dashboardId: string,
    panelIds: string[] = [],
  ): Promise<PanelResult[]> {
    const body = await request<{ results: PanelResult[] }>(
      `/dashboards/${encodeURIComponent(dashboardId)}/refresh`,
      { method: "POST", body: JSON.stringify({ panelIds }) },
    );
    return body.results ?? [];
  },

  async attachPanels(
    dashboardId: string,
    panelIds: string[],
  ): Promise<Dashboard> {
    return request<Dashboard>(
      `/dashboards/${encodeURIComponent(dashboardId)}/panels`,
      { method: "POST", body: JSON.stringify({ panelIds }) },
    );
  },

  /**
   * Downloads an export.
   *
   * Not routed through `request`, which assumes a JSON body: the response here
   * is a binary document.
   */
  async export(
    dashboardId: string,
    format: ExportFormat,
    mode: ExportMode,
    images: { panelId: string; dataUrl: string }[],
  ): Promise<Blob> {
    const response = await fetch(
      `${BASE_URL}/dashboards/${encodeURIComponent(dashboardId)}` +
        `/export/${format}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": readCsrfToken(),
        },
        body: JSON.stringify({ mode, images }),
      },
    );
    if (!response.ok) {
      const body: SafeErrorBody = await response.json().catch(() => ({
        code: "export_failed",
        message: "The export could not be produced.",
      }));
      throw new ApiError(response.status, body);
    }
    return response.blob();
  },
};

/** Charts saved on their own, before they belong to any dashboard. */
export const chartsApi = {
  async list(hidden = false): Promise<Panel[]> {
    return request<Panel[]>(`/charts?hidden=${hidden}`);
  },

  async save(draft: PanelDraft): Promise<Panel> {
    return request<Panel>("/charts", {
      method: "POST",
      body: JSON.stringify(draft),
    });
  },

  /** Takes a chart out of the tray, or puts it back. Never destructive. */
  async setHidden(panelId: string, hidden: boolean): Promise<Panel> {
    return request<Panel>(`/charts/${encodeURIComponent(panelId)}`, {
      method: "PATCH",
      body: JSON.stringify({ hidden }),
    });
  },

  async refresh(panelId: string): Promise<PanelResult> {
    return request<PanelResult>(
      `/charts/${encodeURIComponent(panelId)}/refresh`,
      { method: "POST" },
    );
  },

  /**
   * Deletes a chart outright, wherever it is attached.
   *
   * Only for charts nobody has seen succeed, such as a canvas tile being
   * replaced. Everything else should be hidden, which can be undone.
   */
  async remove(panelId: string): Promise<void> {
    await request<void>(`/charts/${encodeURIComponent(panelId)}`, {
      method: "DELETE",
    });
  },
};

export const authApi = {
  async login(email: string, password: string): Promise<UserSession> {
    const user = await request<SessionUser>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    return toSession(user);
  },

  /** Returns null when no session exists, which is the normal signed-out case. */
  async session(): Promise<UserSession | null> {
    try {
      return toSession(await request<SessionUser>("/auth/session"));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        return null;
      }
      throw error;
    }
  },

  async logout(): Promise<void> {
    try {
      await request<void>("/auth/logout", { method: "POST" });
    } catch (error) {
      // An already-expired session is not a logout failure.
      if (!(error instanceof ApiError) || error.status !== 401) {
        throw error;
      }
    }
  },
};
